package components.auth

import androidx.compose.runtime.*
import com.example.readingnotes.presentation.auth.AuthEvent
import com.example.readingnotes.presentation.auth.AuthViewModel
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import styles.AppStyleSheet
import design.DesignTokens

/**
 * Registration Step 1: Email Input
 * Collect email address with minimal friction
 */
@Composable
fun RegistrationStep1_Email(
    email: String,
    onEmailChange: (String) -> Unit,
    onSendCode: () -> Unit,
    isLoading: Boolean,
    error: String?,
    onCancel: () -> Unit
) {
    var emailError by remember { mutableStateOf<String?>(null) }
    val isValidEmail = remember(email) {
        email.matches(Regex("^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$"))
    }

    Div({ classes(AppStyleSheet.loginContainer) }) {
        Div({ classes(AppStyleSheet.loginForm) }) {
            H2({ classes(AppStyleSheet.loginTitle) }) {
                Text("メールアドレスで登録")
            }

            P({
                style {
                    property("color", DesignTokens.Colors.textSecondary.toString())
                    property("margin-bottom", "24px")
                    property("text-align", "center")
                }
            }) {
                Text("まずメールアドレスを入力してください。認証コードを送信します。")
            }

            // Email input
            Div({ classes(AppStyleSheet.formGroup) }) {
                Label("email-input") {
                    Text("メールアドレス")
                }

                val inputClasses = buildList {
                    add(AppStyleSheet.textInput)
                    if (emailError != null || error != null) add(AppStyleSheet.inputInvalid)
                    else if (isValidEmail) add(AppStyleSheet.inputValid)
                }

                Input(type = InputType.Email) {
                    id("email-input")
                    classes(*inputClasses.toTypedArray())
                    value(email)
                    attr("autocomplete", "email")
                    attr("required", "true")
                    attr("aria-invalid", if (emailError != null || error != null) "true" else "false")
                    onInput {
                        onEmailChange(it.value)
                        emailError = null
                    }
                    if (isLoading) {
                        disabled()
                    }
                }

                // Email validation hint
                if (email.isNotEmpty() && !isValidEmail) {
                    P({
                        id("email-hint")
                        classes(AppStyleSheet.inputHint)
                    }) {
                        Text("有効なメールアドレスを入力してください")
                    }
                }
            }

            // Error messages
            if (emailError != null) {
                val emailErrorMsg = emailError!!
                Div({
                    classes(AppStyleSheet.formError)
                    attr("role", "alert")
                    attr("aria-live", "assertive")
                }) {
                    Text(emailErrorMsg)
                }
            }
            if (error != null) {
                val errorMsg = error!!
                Div({
                    classes(AppStyleSheet.formError)
                    attr("role", "alert")
                    attr("aria-live", "assertive")
                }) {
                    Text(errorMsg)
                }
            }

            // Buttons
            Div({ classes(AppStyleSheet.loginButtonGroup) }) {
                Button({
                    classes(AppStyleSheet.primaryButton)
                    if (!isValidEmail || isLoading) attr("disabled", "true")
                    onClick {
                        if (!isValidEmail) {
                            emailError = "有効なメールアドレスを入力してください"
                        } else {
                            onSendCode()
                        }
                    }
                }) {
                    Text(if (isLoading) "送信中..." else "認証コードを送信")
                }

                Button({
                    classes(AppStyleSheet.secondaryButton)
                    onClick { onCancel() }
                    if (isLoading) disabled()
                }) {
                    Text("キャンセル")
                }
            }
        }
    }
}
